import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Select, TextField, Tooltip } from "@mui/material";
import Icon from "@mdi/react";
import { mdiContentSave, mdiHistory, mdiRestore, mdiAlertCircleOutline } from "@mdi/js";
import {
	NotistackServices,
	PromptAgent,
	PromptTemplateDTO,
	PromptTemplateServices,
	PromptVariant,
} from "shared/services";
import { HandleError } from "shared/utils";
import styles from "./styles.module.scss";

const AGENTS: Array<{ id: PromptAgent; label: string; emoji: string }> = [
	{ id: "assistant", label: "Assistente IA central", emoji: "🤖" },
	{ id: "leader", label: "Líder de Equipe", emoji: "🎯" },
	{ id: "member", label: "Membro Especialista", emoji: "🛠️" },
];

const VARIANTS: Array<{ id: PromptVariant; label: string; description: string }> = [
	{ id: "base", label: "Base", description: "Usado na criação do registro (persistido na entidade)" },
	{ id: "runtime", label: "Runtime", description: "Montado a cada execução com contexto dinâmico (workspace, teams, etc)" },
];

interface SelectedKey {
	agent: PromptAgent;
	variant: PromptVariant;
}

export const PromptsPage: React.FC = () => {
	const [prompts, setPrompts] = useState<PromptTemplateDTO[]>([]);
	const [loadingList, setLoadingList] = useState(true);
	const [selected, setSelected] = useState<SelectedKey>({ agent: "assistant", variant: "runtime" });
	const [draft, setDraft] = useState<string>("");
	const [originalContent, setOriginalContent] = useState<string>("");
	const [notes, setNotes] = useState<string>("");
	const [saving, setSaving] = useState(false);
	const [historyOpen, setHistoryOpen] = useState(false);
	const [history, setHistory] = useState<PromptTemplateDTO[]>([]);
	const [loadingHistory, setLoadingHistory] = useState(false);

	const refresh = useCallback(async () => {
		setLoadingList(true);
		try {
			const data = await PromptTemplateServices.list();
			setPrompts(data);
		} catch (err) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(err) ? err.message : "Erro ao carregar prompts");
		} finally {
			setLoadingList(false);
		}
	}, []);

	useEffect(() => {
		void refresh();
	}, [refresh]);

	const current = useMemo(
		() => prompts.find((p) => p.agent === selected.agent && p.variant === selected.variant) ?? null,
		[prompts, selected],
	);

	useEffect(() => {
		if (current) {
			setDraft(current.content);
			setOriginalContent(current.content);
			setNotes("");
		}
	}, [current?.id]);

	const isDirty = draft !== originalContent;

	const handleSave = async () => {
		if (!isDirty) return;
		setSaving(true);
		try {
			const updated = await PromptTemplateServices.update(selected.agent, selected.variant, draft, notes || undefined);
			setPrompts((prev) => {
				const others = prev.filter((p) => !(p.agent === selected.agent && p.variant === selected.variant));
				return [...others, updated];
			});
			setOriginalContent(updated.content);
			setNotes("");
			NotistackServices.showSuccessMessage(`Versão ${updated.version} salva e ativada`);
		} catch (err) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(err) ? err.message : "Erro ao salvar prompt");
		} finally {
			setSaving(false);
		}
	};

	const openHistory = async () => {
		setHistoryOpen(true);
		setLoadingHistory(true);
		try {
			const data = await PromptTemplateServices.history(selected.agent, selected.variant, 20);
			setHistory(data);
		} catch (err) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(err) ? err.message : "Erro ao carregar histórico");
		} finally {
			setLoadingHistory(false);
		}
	};

	const handleRollback = async (version: number) => {
		setSaving(true);
		try {
			const updated = await PromptTemplateServices.rollback(selected.agent, selected.variant, version);
			setPrompts((prev) => {
				const others = prev.filter((p) => !(p.agent === selected.agent && p.variant === selected.variant));
				return [...others, updated];
			});
			setHistoryOpen(false);
			NotistackServices.showSuccessMessage(`Rollback para versão ${version} aplicado (nova versão ${updated.version} ativa)`);
		} catch (err) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(err) ? err.message : "Erro ao reverter");
		} finally {
			setSaving(false);
		}
	};

	const isFallback = current?.id?.startsWith("__fallback__");

	return (
		<div className={styles.container}>
			<header className={styles.header}>
				<div>
					<h1 className={styles.title}>Prompts dos Agentes IA</h1>
					<p className={styles.subtitle}>
						Edite os system prompts sem precisar de deploy. O ajuste é aplicado em memória imediatamente
						(invalida cache Redis + recarrega instâncias).
					</p>
				</div>
			</header>

			<div className={styles.toolbar}>
				<Select
					size="small"
					value={selected.agent}
					onChange={(e) => setSelected((s) => ({ ...s, agent: e.target.value as PromptAgent }))}
				>
					{AGENTS.map((a) => (
						<MenuItem key={a.id} value={a.id}>
							{a.emoji} {a.label}
						</MenuItem>
					))}
				</Select>

				<Select
					size="small"
					value={selected.variant}
					onChange={(e) => setSelected((s) => ({ ...s, variant: e.target.value as PromptVariant }))}
				>
					{VARIANTS.map((v) => (
						<MenuItem key={v.id} value={v.id}>
							{v.label} — {v.description}
						</MenuItem>
					))}
				</Select>

				<div className={styles.spacer} />

				<Tooltip title="Ver histórico de versões">
					<IconButton onClick={openHistory} disabled={loadingList}>
						<Icon path={mdiHistory} size={1} />
					</IconButton>
				</Tooltip>
			</div>

			{loadingList || !current ? (
				<div className={styles.loading}>
					<CircularProgress />
				</div>
			) : (
				<>
					<div className={styles.metaBar}>
						<Chip
							size="small"
							label={isFallback ? "Fallback (prompts.json)" : `Versão ${current.version} ativa`}
							color={isFallback ? "default" : "success"}
						/>
						<span className={styles.meta}>
							Atualizado em {new Date(current.updatedAt).toLocaleString("pt-BR")}
						</span>
						{current.updatedBy && (
							<span className={styles.meta}>por {current.updatedBy}</span>
						)}
						{isFallback && (
							<span className={styles.warning}>
								<Icon path={mdiAlertCircleOutline} size={0.75} />
								Ainda não foi editado pelo admin — salvando você cria a versão 1
							</span>
						)}
					</div>

					<TextField
						className={styles.editor}
						multiline
						fullWidth
						minRows={24}
						maxRows={50}
						value={draft}
						onChange={(e) => setDraft(e.target.value)}
						variant="outlined"
						InputProps={{ style: { fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.85rem" } }}
					/>

					<div className={styles.notesRow}>
						<TextField
							size="small"
							fullWidth
							label="Anotação (opcional) — descreva a mudança"
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
						/>
					</div>

					<div className={styles.actions}>
						<Button
							variant="outlined"
							onClick={() => {
								setDraft(originalContent);
								setNotes("");
							}}
							disabled={!isDirty || saving}
						>
							Descartar
						</Button>
						<Button
							variant="contained"
							onClick={handleSave}
							disabled={!isDirty || saving}
							startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Icon path={mdiContentSave} size={0.9} />}
						>
							{saving ? "Salvando…" : isFallback ? "Criar versão 1" : "Salvar nova versão"}
						</Button>
					</div>
				</>
			)}

			<Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
				<DialogTitle>
					Histórico — {selected.agent} / {selected.variant}
				</DialogTitle>
				<DialogContent dividers>
					{loadingHistory ? (
						<div className={styles.loading}>
							<CircularProgress size={32} />
						</div>
					) : history.length === 0 ? (
						<p>Sem histórico para este prompt.</p>
					) : (
						<table className={styles.historyTable}>
							<thead>
								<tr>
									<th>Versão</th>
									<th>Ativa</th>
									<th>Quando</th>
									<th>Por</th>
									<th>Notas</th>
									<th>Ações</th>
								</tr>
							</thead>
							<tbody>
								{history.map((h) => (
									<tr key={h.id} className={h.isActive ? styles.activeRow : ""}>
										<td>v{h.version}</td>
										<td>{h.isActive ? "✅" : ""}</td>
										<td>{new Date(h.updatedAt).toLocaleString("pt-BR")}</td>
										<td>{h.updatedBy ?? "—"}</td>
										<td className={styles.notesCell}>{h.notes ?? ""}</td>
										<td>
											{!h.isActive && (
												<Tooltip title={`Restaurar v${h.version}`}>
													<IconButton size="small" onClick={() => handleRollback(h.version)} disabled={saving}>
														<Icon path={mdiRestore} size={0.8} />
													</IconButton>
												</Tooltip>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setHistoryOpen(false)}>Fechar</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};
